import { CampaignType, CampaignTypes } from '@features/campaigns/shared/types/campaign.ts';
import { AccessRight, AccessRights } from '@shared/api/commonApiInterfaces.ts';
import { useUserProfileByIdData } from '@shared/hooks/useUserProfileByIdData.ts';
import { useUserProfileData } from '@shared/hooks/useUserProfileData.ts';
import { useUserDetails } from '@shared/providers/UserDetailsProvider/useUserDetails.tsx';
import { PossibleOnboardingState } from '@shared/types/userProfile.types.ts';
import { UserId } from '@shared/types/users.ts';

interface UseUserOnboardingState {
  campaignType?: CampaignType;
  campaignCreatedBy?: UserId;
  accessRights?: AccessRight[];
}

interface UseUserOnboardingStateOutput {
  isOnboardingCompleted: boolean;
  onboardingState: PossibleOnboardingState | undefined;
}

export function useUserOnboardingState({
  campaignType,
  campaignCreatedBy,
  accessRights,
}: UseUserOnboardingState): UseUserOnboardingStateOutput {
  const { isExternalDirectClient, isExternalAgency } = useUserDetails();

  const isCurrentUserExternal = isExternalDirectClient || isExternalAgency;

  const canInternalUserGetExternalUserOnboardingStatus =
    !isCurrentUserExternal &&
    campaignType === CampaignTypes.SELF_SERVICE &&
    Boolean(accessRights?.includes(AccessRights.EDIT_AGENCY)) &&
    Boolean(accessRights?.includes(AccessRights.EDIT_PURCHASE_ORDER));

  const { isOnboardingCompleted: isExternalUserOnboardingCompletedForExternalUser, onboardingState } =
    useUserProfileData({
      canFetchUserProfileData: isCurrentUserExternal,
    });

  const { isOnboardingCompleted: isExternalUserOnboardingCompletedForInternalUser } = useUserProfileByIdData({
    id: campaignCreatedBy || '',
    canFetchData: canInternalUserGetExternalUserOnboardingStatus,
  });

  const isOnboardingCompleted = Boolean(
    isExternalUserOnboardingCompletedForExternalUser || isExternalUserOnboardingCompletedForInternalUser,
  );

  return {
    isOnboardingCompleted,
    onboardingState,
  };
}
